/**
 * twiglette — build Twig templates from a JS interface, without writing Twig.
 *
 * Attaches to <input> and <textarea> fields and inserts Twig source via a
 * point-and-click UI: plain fields, for-loops, and common filters, with live
 * syntax highlighting for {{ ... }}, {% ... %}, strings, and comments.
 *
 * Public API:
 *   TwigBuilder.init(selector, options)    — attach to every matching element
 *   TwigBuilder.attach(element, options)   — attach to a single element
 *   TwigBuilder.get(element)               — fetch an attached instance, or null
 *   instance.openInsertMenu()              — open the insertion dropdown
 *                                            programmatically (for custom CTAs)
 *
 * Options:
 *   data       — JSON shape the template will render against; drives variable
 *                pickers, for-loop list choices, and scoped fields.
 *   filters         — array controlling which filters the chain dropdown
 *                     exposes. Accepts built-in names (e.g. ["upper", "date"])
 *                     or full specs ({ name, arg?, desc? }). Omit for all
 *                     built-ins; pass [] to suppress the filter chip entirely.
 *   showButton      — set to false to suppress the built-in CTA button. The
 *                     consumer is then responsible for rendering their own and
 *                     calling `instance.openInsertMenu()` on its click.
 *   addButtonLabel  — label for the built-in CTA. Translated string in;
 *                     defaults to "Add Twig" when not supplied.
 *   openMenuOnBraces — when true, typing `{{` (or `{%`) swaps the braces for
 *                      a pending placeholder and opens the field dropdown
 *                      (or the tag flow) at that spot. Escape or clicking away
 *                      reverts the placeholder back to the literal braces so
 *                      manual typing isn't hijacked.
 *   allowTags  — set to false for hosts whose sandbox forbids `{% %}` tags:
 *                hides the Logic menu section and ignores typed `{%`.
 *
 * Example:
 *   TwigBuilder.init('.my-twig-field', {
 *     data: orderData,
 *     filters: ['upper', 'lower', 'date', 'number_format'],
 *   });
 */

import CodeEditor from './CodeEditor.js';
import {Decoration, EditorView, ViewPlugin} from '@codemirror/view';
import {RangeSetBuilder} from '@codemirror/state';
import {buildFilterEdit, findFilterTarget, scanTwigSegments, splitFilterParts} from './TwigExpression.js';

const PLACEHOLDER = '{{ Please Select }}';
const CHIP_BLEED = '3px';

// English fallbacks for dropdown-menu strings. Consumers pass `labels` to
// `TwigBuilder.attach()` to override; partial maps are merged on top of these.
const DEFAULT_LABELS = {
  sectionValues:          'Values',
  sectionLogic:           'Logic',
  plainField:             'Plain Field',
  typeValue:              'Type a value…',
  forLoop:                'For Loop',
  ifElse:                 'If / Else',
  ifConditionHeading:     'Condition',
  ifIncludeElse:          'Include else',
  scopeFieldSuffix:       'Field',
  back:                   '← Back',
  next:                   'Next',
  insert:                 'Insert',
  apply:                  'Apply',
  done:                   'Done',
  forLoopIncludeKey:      'Include key',
  enterManually:          'Enter Manually',
  listWontRender:         "list — won't render directly",
  manualInputHeading:     'Variable name',
  literalInputHeading:    'Literal value',
  mapEnterKey:            'enter a name…',
  mapKeyHeading:          'Custom field name',
  mapKeyPlaceholder:      'e.g. gift_message',
  integrationFieldPick:   'choose a field…',
  integrationFieldTitle:  'Integration field',
  filterChip:             '+ Twig Filter',
  filterChipTooltip:      'Twig filters are modifiers that change how this value is output',
  filterMenuHint:         'Filters are modifiers: they transform the value they follow, like making text uppercase or formatting a date.',
};

const SEGMENT_CLASS = {
  pending: 'tw-hl tw-hl--pending',
  var:     'tw-hl tw-hl--var',
  ctrl:    'tw-hl tw-hl--ctrl',
  comment: 'tw-hl tw-hl--comment',
};

const twigSyntaxDecorations = ViewPlugin.fromClass(
  class {
    constructor(view) {
      this.decorations = this._build(view);
    }

    update(update) {
      if (update.docChanged || update.viewportChanged) {
        this.decorations = this._build(update.view);
      }
    }

    _build(view) {
      const builder = new RangeSetBuilder();
      const segments = scanTwigSegments(view.state.doc.toString());

      for (const seg of segments) {
        const spec = {class: SEGMENT_CLASS[seg.kind]};

        if (seg.kind === 'pending') {
          spec.attributes = {'data-pending-start': String(seg.start)};
        }

        builder.add(seg.start, seg.end, Decoration.mark(spec));
      }

      return builder.finish();
    }
  },
  {decorations: v => v.decorations},
);

let nextTokenId = 1;
const placeholders = new Map(); // tokenId -> { instance, start, end }

// Only one menu may be open across all instances — the typed-brace trigger
// opens menus without a mousedown, so outside-click dismissal can't enforce it.
let activeDropdownInstance = null;

// ---- Minimal Twig tokenizer for the highlight overlay --------------------

function escHtml(s) {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// ---- Path resolution helpers --------------------------------------------

function resolveByPath(data, path) {
  if (!path) return data;
  let cur = data;
  let i = 0;
  while (i < path.length) {
    if (path[i] === '.') { i++; continue; }
    let key;
    if (path[i] === '[') {
      const endB = path.indexOf(']', i);
      if (endB === -1) return undefined;
      key = path.slice(i + 1, endB).trim();
      if ((key.startsWith("'") && key.endsWith("'")) ||
          (key.startsWith('"') && key.endsWith('"'))) {
        key = key.slice(1, -1).replace(/\\(.)/g, '$1');
      }
      i = endB + 1;
    } else {
      let j = i;
      while (j < path.length && path[j] !== '.' && path[j] !== '[') j++;
      key = path.slice(i, j);
      i = j;
    }
    if (cur === null || cur === undefined) return undefined;
    cur = cur[key];
  }
  return cur;
}

// Common Twig filters. `arg` marks a filter that takes a single argument; the
// inline prompt label comes from `arg`. Multi-arg filters (replace, etc.) are
// omitted for now — users can add them manually.
const TWIG_FILTERS = [
  { name: 'upper',         desc: 'Uppercase' },
  { name: 'lower',         desc: 'Lowercase' },
  { name: 'capitalize',    desc: 'Capitalize first letter' },
  { name: 'title',         desc: 'Title Case' },
  { name: 'trim',          desc: 'Strip whitespace' },
  { name: 'digits',        desc: 'Keep only digits' },
  { name: 'length',        desc: 'String length / array count' },
  { name: 'default',       arg: 'fallback value', desc: 'Fallback when empty' },
  { name: 'date',          arg: 'format (e.g. Y-m-d)', desc: 'Format a date' },
  { name: 'number_format', arg: 'decimals', desc: 'Format a number' },
  { name: 'escape',        desc: 'HTML escape (alias: e)' },
  { name: 'raw',           desc: 'Disable escaping' },
  { name: 'url_encode',    desc: 'URL encode' },
  { name: 'json_encode',   desc: 'JSON encode' },
  { name: 'striptags',     desc: 'Strip HTML tags' },
  { name: 'first',         desc: 'First item of an iterable' },
  { name: 'last',          desc: 'Last item of an iterable' },
  { name: 'reverse',       desc: 'Reverse a string/array' },
  { name: 'abs',           desc: 'Absolute value' },
  { name: 'join',          arg: 'separator (e.g. ", ")', desc: 'Join array' },
  { name: 'split',         arg: 'separator (e.g. ",")',  desc: 'Split string into array' },
  { name: 'nl2br',         desc: 'Newlines → <br>' },
  { name: 'barcode',       desc: 'Barcode image' },
  { name: 'qrcode',        desc: 'QR code image' },
];

// Resolve the `filters` init option into concrete filter specs. Accepts:
//   - undefined/null → all built-ins
//   - array of strings → matching built-ins only
//   - array of { name, arg?, desc? } → custom specs used as-is
//   - mixed → each element resolved independently
// Unknown names / malformed specs emit a console warning and are skipped.
function resolveFilters(opt) {
  if (opt === undefined || opt === null) return TWIG_FILTERS.slice();
  if (!Array.isArray(opt)) {
    // eslint-disable-next-line no-console
    console.warn('[twiglette] `filters` option must be an array — defaulting to all built-ins');
    return TWIG_FILTERS.slice();
  }
  const resolved = [];
  for (const f of opt) {
    if (typeof f === 'string') {
      const builtIn = TWIG_FILTERS.find((b) => b.name === f);
      if (builtIn) resolved.push(builtIn);
      // eslint-disable-next-line no-console
      else console.warn(`[twiglette] unknown built-in filter "${f}"`);
    } else if (f && typeof f === 'object' && typeof f.name === 'string') {
      resolved.push(f);
    } else {
      // eslint-disable-next-line no-console
      console.warn('[twiglette] invalid filter spec', f);
    }
  }
  return resolved;
}

// Normalize a typed literal to a valid Twig expression. Strings get wrapped
// in single quotes (with any embedded ' escaped); numbers / booleans / null /
// already-quoted values are kept as-is.
function formatTwigLiteral(raw) {
  const v = raw.trim();
  if (!v) return '';
  if (/^-?\d+(\.\d+)?$/.test(v)) return v;
  if (/^(true|false|null)$/i.test(v)) return v.toLowerCase();
  if ((v.startsWith("'") && v.endsWith("'") && v.length >= 2) ||
      (v.startsWith('"') && v.endsWith('"') && v.length >= 2)) return v;
  return `'${v.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;
}

// Derive a human-friendly primary-dropdown label for a scope's fields.
// e.g. listPath "order_items" + suffix "Field" -> "Items Field"
function scopeFieldLabel(listPath, suffix) {
  const last = String(listPath).split(/[._]/).filter(Boolean).pop() || 'item';
  return last.charAt(0).toUpperCase() + last.slice(1) + ' ' + suffix;
}

// Parse the text up to `pos` and return the innermost for-loop scope the
// cursor is inside, or null. `resolveShape(listPath, parentScope) -> itemShape`
// resolves the item shape for a new scope from the JSON data.
function detectScopeAtPosition(text, pos, resolveShape) {
  const re = /\{%\s*(for|endfor)\b([^%]*)%\}/g;
  const stack = [];
  let m;
  while ((m = re.exec(text)) !== null) {
    const tagEnd = m.index + m[0].length;
    // Stop once we reach (or fall inside) the insertion point
    if (m.index >= pos || tagEnd > pos) break;
    if (m[1] === 'for') {
      const body = m[2].trim();
      const parsed = body.match(/^(\w+)(?:\s*,\s*(\w+))?\s+in\s+(.+?)\s*$/);
      if (!parsed) continue;
      let keyVar = null, loopVar;
      if (parsed[2]) { keyVar = parsed[1]; loopVar = parsed[2]; }
      else { loopVar = parsed[1]; }
      const listPath = parsed[3].trim();
      const parent = stack[stack.length - 1] || null;
      const shape = resolveShape(listPath, parent);
      stack.push({ loopVar, keyVar, listPath, shape, parent });
    } else {
      stack.pop();
    }
  }
  return stack[stack.length - 1] || null;
}

// ---- JSON shape flattening (Phase 3) -------------------------------------
//
// Walks the data and produces a flat list of entries for the Plain Field
// dropdown. Objects are traversed transparently (we only list leaves). Arrays
// are emitted as a "list" marker, then their indexed sub-paths are emitted up
// to `maxIndex`. `depth` is used purely for visual indentation of list items.

const IDENTIFIER_RE = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

// Build a Twig-valid path. Keys that aren't bare identifiers (spaces, dashes,
// special chars) switch to bracket notation so "checkout_data.Gift Message"
// becomes "checkout_data['Gift Message']".
function joinPath(prefix, key) {
  const k = String(key);
  if (IDENTIFIER_RE.test(k)) return prefix ? `${prefix}.${k}` : k;
  const esc = k.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
  return prefix ? `${prefix}['${esc}']` : `['${esc}']`;
}

// `isBranch` marks an object path offered as an entry of its own ahead of its
// leaves, for nodes a dedicated step builds paths under.
function flattenShape(obj, opts = {}) {
  const { prefix = '', depth = 0, maxIndex = 5, maxDepth = 15, isBranch = () => false } = opts;
  const out = [];
  if (obj === null || obj === undefined) return out;
  if (depth > maxDepth) return out;

  if (Array.isArray(obj)) {
    obj.slice(0, maxIndex).forEach((item, i) => {
      const path = prefix ? `${prefix}.${i}` : String(i);
      if (item !== null && typeof item === 'object' && !Array.isArray(item)) {
        out.push(...flattenShape(item, { prefix: path, depth: depth + 1, maxIndex, maxDepth, isBranch }));
      } else if (Array.isArray(item)) {
        out.push({ path, kind: 'list', depth: depth + 1 });
        out.push(...flattenShape(item, { prefix: path, depth: depth + 1, maxIndex, maxDepth, isBranch }));
      } else {
        out.push({ path, kind: 'scalar', depth: depth + 1 });
      }
    });
    return out;
  }

  if (typeof obj === 'object') {
    for (const key of Object.keys(obj)) {
      const val = obj[key];
      const path = joinPath(prefix, key);
      if (Array.isArray(val)) {
        out.push({ path, kind: 'list', depth });
        out.push(...flattenShape(val, { prefix: path, depth: depth + 1, maxIndex, maxDepth, isBranch }));
      } else if (val !== null && typeof val === 'object') {
        if (isBranch(path)) out.push({ path, kind: 'branch', depth });
        out.push(...flattenShape(val, { prefix: path, depth, maxIndex, maxDepth, isBranch }));
      } else {
        out.push({ path, kind: 'scalar', depth });
      }
    }
    return out;
  }

  return out;
}

// ---- Twig tokenizer for the highlight overlay ----------------------------

function tokenizeTwig(text, activeRange = null) {
  let html = '';
  let i = 0;
  const n = text.length;
  while (i < n) {
    if (text[i] === '{' && text[i + 1] === '{') {
      const end = text.indexOf('}}', i + 2);
      if (end === -1) { html += escHtml(text.slice(i)); break; }
      const seg = text.slice(i, end + 2);
      const isPending = seg.toLowerCase().includes('please select');
      const classes = ['tw-hl'];
      classes.push(isPending ? 'tw-hl--pending' : 'tw-hl--var');
      if (isPending && activeRange && activeRange.start === i) {
        classes.push('tw-hl--active');
      }
      const attrs = isPending
        ? ` data-pending-start="${i}"`
        : ` data-var-start="${i}" data-var-end="${end + 2}"`;
      if (isPending) {
        html += `<span class="${classes.join(' ')}"${attrs}>${escHtml(seg)}</span>`;
      } else {
        // Sub-tokenize: variable path in one color, each |filter chunk in another.
        const parts = splitFilterParts(seg.slice(2, -2));
        const innerHtml = parts
          .map((p, idx) =>
            idx === 0
              ? escHtml(p)
              : `<span class="tw-hl--filter">${escHtml(p)}</span>`
          )
          .join('');
        html += `<span class="${classes.join(' ')}"${attrs}>` +
          `<span class="tw-hl--brace">{{</span>${innerHtml}<span class="tw-hl--brace">}}</span></span>`;
      }
      i = end + 2;
      continue;
    }
    if (text[i] === '{' && text[i + 1] === '%') {
      const end = text.indexOf('%}', i + 2);
      if (end === -1) { html += escHtml(text.slice(i)); break; }
      const seg = text.slice(i, end + 2);
      if (seg.toLowerCase().includes('please select')) {
        const classes = ['tw-hl', 'tw-hl--pending'];
        if (activeRange && activeRange.start === i) classes.push('tw-hl--active');
        html += `<span class="${classes.join(' ')}" data-pending-start="${i}">${escHtml(seg)}</span>`;
      } else {
        html += `<span class="tw-hl tw-hl--ctrl">${escHtml(seg)}</span>`;
      }
      i = end + 2;
      continue;
    }
    // Plain text up to the next potential opener
    const next = text.indexOf('{', i + 1);
    if (next === -1) { html += escHtml(text.slice(i)); break; }
    html += escHtml(text.slice(i, next));
    i = next;
  }
  return html;
}

// Quacks like a textarea but delegates value, selection, and DOM hooks to a
// mounted CodeMirror EditorView. Lets the rest of TwigBuilder stay unchanged
// when attached to a CM-backed field — overlay-rendering code paths are
// gated by `target._isCmProxy` and skipped, since CM owns the visual layer.
class CmEditorProxy {
  constructor(codeEditor, originalTextarea) {
    this._cm        = codeEditor;
    this._textarea  = originalTextarea;
    this._listeners = new Map();
    this._style     = {};

    this.tagName    = 'TEXTAREA';
    this._isCmProxy = true;
  }

  get value() { return this._cm.value; }
  set value(v) { this._cm.value = v; }

  get selectionStart() { return this._cm.getSelection().start; }
  get selectionEnd()   { return this._cm.getSelection().end; }

  get scrollTop()  { return this._cm.view.scrollDOM.scrollTop; }
  get scrollLeft() { return this._cm.view.scrollDOM.scrollLeft; }

  get parentNode() { return this._cm.host.parentNode; }
  get style()      { return this._style; }

  setRangeText(text, start, end /* , _mode */) {
    this._cm.replaceRange(start, end, text);
  }

  setSelectionRange(start, end) {
    this._cm.setSelection(start, end);
  }

  focus() { this._cm.focus(); }

  dispatchEvent() { return true; }

  addEventListener(name, cb) {
    if (!this._listeners.has(name)) this._listeners.set(name, new Set());
    this._listeners.get(name).add(cb);
  }

  removeEventListener(name, cb) {
    this._listeners.get(name)?.delete(cb);
  }

  getBoundingClientRect() {
    return this._cm.view.dom.getBoundingClientRect();
  }
}

const TwigBuilder = {
  _instances: new WeakMap(),

  attach(target, options = {}) {
    if (
      !(target instanceof HTMLInputElement) &&
      !(target instanceof HTMLTextAreaElement)
    ) {
      throw new Error('TwigBuilder.attach: target must be <input> or <textarea>');
    }
    if (this._instances.has(target)) return this._instances.get(target);
    try {
      const codeEditor = target instanceof HTMLTextAreaElement ? CodeEditor.get(target) : null;
      const editor     = codeEditor ? new CmEditorProxy(codeEditor, target) : target;
      const instance   = new TwigBuilderInstance(editor, options);

      this._instances.set(target, instance);

      return instance;
    } catch (err) {
      // Don't let a single broken field break the host page.
      // eslint-disable-next-line no-console
      console.error('[twiglette] failed to attach', err);
      return null;
    }
  },

  init(selector, options = {}) {
    if (typeof selector !== 'string' || !selector) {
      throw new Error('TwigBuilder.init: selector must be a non-empty CSS string');
    }
    document.querySelectorAll(selector).forEach((el) => {
      this.attach(el, options);
    });
  },

  // Look up an existing instance for a target. Useful for consumers that
  // attach with `{showButton: false}` and want to wire their own CTA to
  // `instance.openInsertMenu()` from a separate code path.
  get(target) {
    return this._instances.get(target) ?? null;
  },

  _placeholders: placeholders, // exposed for future phases
};

function discardDeadCodeMirrorChrome(outer, toolbarSlot) {
  const previous = outer.previousElementSibling;

  if (toolbarSlot) {
    toolbarSlot.querySelector('.twig-builder-btn-floater')?.remove();
  } else if (previous?.classList.contains('twig-builder-btn-floater')) {
    previous.remove();
  }

  outer.querySelector(':scope > .twig-builder-btn--corner')?.remove();
  outer.querySelector(':scope > .twig-builder-triggers')?.remove();
}

class TwigBuilderInstance {
  constructor(target, options) {
    this.target = target;
    this.data = options.data || {};
    this.fieldTypes = options.fieldTypes || {};
    this.integrationFieldUrls = options.integrationFieldUrls || {};
    this.dateTimeFormat = options.dateTimeFormat || null;
    this.filters = resolveFilters(options.filters);
    this.isTextarea = target.tagName === 'TEXTAREA';
    this._cm = target._isCmProxy ? target._cm : null;
    // `showButton: false` lets consumers replace the built-in "Add Twig"
    // CTA with one they render themselves. They wire their button's click
    // to `instance.openInsertMenu()` to fire the same flow.
    this.showButton = options.showButton !== false;
    this.addButtonLabel = options.addButtonLabel || 'Add Twig';
    this.addButtonIcon = options.addButtonIcon || null;
    this.cornerButton = options.cornerButton === true;
    this.labels = {...DEFAULT_LABELS, ...(options.labels || {})};
    this._placeholder = options.placeholderToken || PLACEHOLDER;
    this._tagPlaceholder = this._placeholder.replace('{{', '{%').replace('}}', '%}');
    this.openMenuOnBraces = options.openMenuOnBraces === true;
    this.allowTags = options.allowTags !== false;
    this._suppressBraceTrigger = false;
    this.button = null;
    this.dropdown = null;
    this.activeTokenId = null;
    this._filterRange = null;

    this._buildUI();
    this._bindEvents();
  }

  // ---- UI scaffolding (Phase 1) ------------------------------------------

  _buildUI() {
    if (this._cm) {
      this._buildUICodeMirror();
    } else {
      this._buildUIOverlay();
    }
  }

  _buildUIOverlay() {
    const deadWrapper = this.target.parentNode;

    if (deadWrapper.classList.contains('twig-builder-wrapper')) {
      deadWrapper.replaceWith(this.target);
    }

    const wrapper = document.createElement('span');
    wrapper.className =
      'twig-builder-wrapper ' +
      (this.isTextarea
        ? 'twig-builder-wrapper--textarea'
        : 'twig-builder-wrapper--input');
    this.target.parentNode.insertBefore(wrapper, this.target);
    wrapper.appendChild(this.target);
    this.wrapper = wrapper;

    // Highlight overlay — rendered *behind* the field. The field itself is
    // made transparent so only the overlay paints the visible text.
    const overlay = document.createElement('div');
    overlay.className =
      'twig-builder-overlay ' +
      (this.isTextarea
        ? 'twig-builder-overlay--textarea'
        : 'twig-builder-overlay--input');
    overlay.setAttribute('aria-hidden', 'true');
    wrapper.insertBefore(overlay, this.target);
    this.overlay = overlay;

    // Filter trigger layer — sits above the input so + buttons are clickable.
    // The layer itself is pointer-events:none; only the buttons are interactive.
    const triggers = document.createElement('div');
    triggers.className = 'twig-builder-triggers';
    triggers.setAttribute('aria-hidden', 'true');
    wrapper.appendChild(triggers);
    this.triggers = triggers;

    if (this.showButton) {
      this.button = this._createAddButton();
      wrapper.appendChild(this.button);
    }
    if (this.filters.length > 0) this._ensureActiveChip();

    // Initial paint. Reserve padding-right after layout so the button
    // doesn't sit on top of typed text.
    requestAnimationFrame(() => {
      this._reserveButtonSpace();
      this._syncOverlayStyles();
      this._renderOverlay();
    });
  }

  // CodeMirror path: no overlay or hotspots — CM owns the visible text and
  // applies a Decoration plugin to mark `{{ please select }}` ranges with the
  // pending-placeholder class. Clicks on those ranges reopen the dropdown.
  _buildUICodeMirror() {
    const host  = this._cm.host;
    const outer = host.parentNode;

    outer.classList.add('twig-builder-wrapper--code-editor');
    this.wrapper = outer;
    this.overlay = null;

    const floater = document.createElement('div');
    floater.className = 'twig-builder-btn-floater';

    const toolbar = document.createElement('div');
    toolbar.className = 'twig-builder-toolbar';
    floater.appendChild(toolbar);

    const toolbarSlot = outer.closest('.template-content-label')?.querySelector('[data-twig-toolbar-slot]');

    discardDeadCodeMirrorChrome(outer, toolbarSlot);

    const triggers = document.createElement('div');
    triggers.className = 'twig-builder-triggers';
    triggers.setAttribute('aria-hidden', 'true');
    outer.appendChild(triggers);
    this.triggers = triggers;

    if (toolbarSlot) {
      toolbarSlot.appendChild(floater);
    } else {
      outer.parentNode.insertBefore(floater, outer);
    }

    this.floaterEl = floater;

    if (this.showButton) {
      this.button = this._createAddButton();

      if (this.cornerButton) {
        this.button.classList.add('twig-builder-btn--corner');
        outer.appendChild(this.button);
      } else {
        toolbar.appendChild(this.button);
      }
    }

    this._cm.reconfigure([
      twigSyntaxDecorations,
      EditorView.domEventHandlers({
        mousedown: (event, view) => this._handleCmMouseDown(event, view),
      }),
    ]);
  }

  _createAddButton() {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'twig-builder-btn';
    button.textContent = this.addButtonIcon || this.addButtonLabel;
    button.setAttribute('aria-label', this.addButtonLabel);
    button.setAttribute('aria-haspopup', 'listbox');
    button.addEventListener('mousedown', (e) => e.preventDefault());

    return button;
  }

  _handleCmMouseDown(event) {
    const el = event.target instanceof Element
      ? event.target.closest('.tw-hl--pending')
      : null;

    if (!el) {
      return false;
    }

    const start = parseInt(el.dataset.pendingStart, 10);

    if (isNaN(start)) {
      return false;
    }

    const tokenId = this._findPendingTokenIdAt(start);

    if (!tokenId) {
      return false;
    }

    event.preventDefault();
    this._reopenToken(tokenId);

    return true;
  }

  // Ensure typed text doesn't flow under the absolutely-positioned button.
  _reserveButtonSpace() {
    if (this._cm || !this.button) return;
    const btnW = this.button.getBoundingClientRect().width;
    const reserve = Math.ceil(btnW) + 14;
    const computed = getComputedStyle(this.target);
    const existing = parseFloat(computed.paddingRight) || 0;
    if (existing < reserve) {
      this.target.style.paddingRight = reserve + 'px';
    }
  }

  _syncOverlayStyles() {
    if (!this.overlay) return;
    const c = getComputedStyle(this.target);
    const o = this.overlay.style;
    const copy = [
      'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'fontVariant',
      'letterSpacing', 'textTransform', 'textIndent', 'textAlign',
      'lineHeight', 'tabSize',
      'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
      'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
      'boxSizing',
    ];
    for (const p of copy) o[p] = c[p];
    // A scrolled div paints overflow into its padding where an input clips it,
    // which would show text around the corner controls.
    if (!this.isTextarea) {
      const right = `calc(${c.paddingRight} + ${c.borderRightWidth} - ${CHIP_BLEED})`;
      const left = `calc(${c.paddingLeft} + ${c.borderLeftWidth} - ${CHIP_BLEED})`;
      o.clipPath = `inset(0 ${right} 0 ${left})`;
    }
    // Preserve the field's background behind the transparent input.
    o.backgroundColor = c.backgroundColor;
  }

  _renderOverlay() {
    if (!this.overlay) return;
    // Append a trailing space so a final newline is rendered (textarea behavior).
    this.overlay.innerHTML = tokenizeTwig(this.target.value, this._activeRange()) + ' ';
    this._syncOverlayScroll();
    this._updateActiveChip();
    this._updatePendingHotspots();
  }

  // Locate the {{ … }} that contains (or is directly adjacent to) the caret.
  // Returns { start, end } in text coordinates, or null. Skips pending
  // `{{ please select }}` — those are being configured via their own dropdown.
  _activeVarRange() {
    const target = this.target;
    if (document.activeElement !== target) return null;
    if (target.selectionStart !== target.selectionEnd) return null;
    const pos = target.selectionStart ?? 0;
    const value = target.value;
    let i = 0;
    while (i < value.length) {
      const start = value.indexOf('{{', i);
      if (start === -1) break;
      const end = value.indexOf('}}', start + 2);
      if (end === -1) break;
      const endPos = end + 2;
      if (pos >= start && pos <= endPos) {
        const seg = value.slice(start, endPos);
        if (!seg.toLowerCase().includes('please select')) return { start, end: endPos };
      }
      i = endPos;
    }
    return null;
  }

  _ensureActiveChip() {
    if (this.activeChip) return this.activeChip;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = this._cm ? 'tw-filter-trigger' : 'twig-builder-btn twig-builder-btn--secondary twig-builder-btn--filter';
    btn.textContent = this._cm ? this.labels.filterChip : '|';
    btn.title = this.labels.filterChipTooltip;
    btn.setAttribute('aria-label', this.labels.filterChipTooltip);
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.style.display = 'none';
    btn.addEventListener('mousedown', (e) => e.preventDefault());
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const start = parseInt(btn.dataset.varStart, 10);
      const end = parseInt(btn.dataset.varEnd, 10);
      if (!isNaN(start) && !isNaN(end)) {
        this._openFilterDropdown({ start, end });
      }
    });
    this.triggers.appendChild(btn);
    this.activeChip = btn;
    return btn;
  }

  _hideActiveChip() {
    if (this.activeChip) this.activeChip.style.display = 'none';
  }

  // Show the single filter chip while the caret is in a variable. Inputs and
  // textareas keep it in the corner; the code editor floats it by the variable.
  _updateActiveChip() {
    if (!this.triggers || !this.wrapper) return;
    if (this._cm) {
      const range = this._cmChipRange();
      if (!range) {
        this._hideActiveChip();
        this._cm.view.requestMeasure({ key: this, read: () => null, write: () => this._hideActiveChip() });
        return;
      }
      const chip = this._ensureActiveChip();
      chip.dataset.varStart = range.start;
      chip.dataset.varEnd = range.end;
      this._cm.view.requestMeasure({
        key:   this,
        read:  () => this._cmChipRect(range),
        write: (rect) => this._placeActiveChip(rect ? { range, rect } : null),
      });
      return;
    }
    if (!this.overlay) return;
    const range = this.filters.length > 0 ? this._activeVarRange() : null;
    this._markTargetedVar(range);
    if (!range) { this._hideActiveChip(); return; }

    const chip = this._ensureActiveChip();
    chip.dataset.varStart = range.start;
    chip.dataset.varEnd = range.end;
    chip.style.display = '';
  }

  _markTargetedVar(range) {
    this.overlay.querySelector('.tw-hl--targeted')?.classList.remove('tw-hl--targeted');
    if (!range) return;
    this.overlay
      .querySelector(`.tw-hl--var[data-var-start="${range.start}"][data-var-end="${range.end}"]`)
      ?.classList.add('tw-hl--targeted');
  }

  _cmChipRange() {
    const view = this._cm.view;
    if (this.filters.length === 0 || !view.hasFocus) return null;

    const { from, to } = view.state.selection.main;

    return findFilterTarget(view.state.doc.toString(), from, to);
  }

  _cmChipRect(range) {
    const view = this._cm.view;
    if (!view.hasFocus) return null;

    const rect = view.coordsAtPos(range.start, 1);
    const visible = view.scrollDOM.getBoundingClientRect();
    if (!rect || rect.top < visible.top || rect.bottom > visible.bottom) return null;

    return rect;
  }

  _placeActiveChip(anchor) {
    // No configured filters → nothing to offer, so skip the chip entirely.
    if (this.filters.length === 0 || !anchor) { this._hideActiveChip(); return; }

    const chip = this._ensureActiveChip();
    chip.dataset.varStart = anchor.range.start;
    chip.dataset.varEnd = anchor.range.end;
    chip.style.display = '';

    const wrapRect = this.wrapper.getBoundingClientRect();
    const chipH = chip.offsetHeight || 22;
    const gap = 4;

    // Prefer above; fall back below when we'd clip above the field's top edge.
    const topIfAbove = anchor.rect.top - wrapRect.top - chipH - gap;
    const topIfBelow = anchor.rect.bottom - wrapRect.top + gap;
    chip.style.top = `${topIfAbove >= 0 ? topIfAbove : topIfBelow}px`;
    chip.style.left = `${anchor.rect.left - wrapRect.left}px`;
  }

  // Transparent hotspots layered over each `{{ please select }}` so the user
  // can click one to re-open its configuration dropdown after dismissing it.
  // Pool-reuses DOM elements; grows/shrinks to match pending-span count.
  _updatePendingHotspots() {
    if (!this.triggers || !this.overlay || !this.wrapper) return;
    const spans = this.overlay.querySelectorAll('.tw-hl--pending');
    if (!this._pendingHotspots) this._pendingHotspots = [];

    while (this._pendingHotspots.length > spans.length) {
      this._pendingHotspots.pop().remove();
    }
    while (this._pendingHotspots.length < spans.length) {
      this._pendingHotspots.push(this._createPendingHotspot());
    }

    const wrapRect = this.wrapper.getBoundingClientRect();
    spans.forEach((span, i) => {
      const hotspot = this._pendingHotspots[i];
      const start = parseInt(span.dataset.pendingStart, 10);
      hotspot.dataset.pendingStart = isNaN(start) ? '' : String(start);
      const rect = span.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) {
        hotspot.style.display = 'none';
        return;
      }
      hotspot.style.display = '';
      hotspot.style.left = `${rect.left - wrapRect.left}px`;
      hotspot.style.top = `${rect.top - wrapRect.top}px`;
      hotspot.style.width = `${rect.width}px`;
      hotspot.style.height = `${rect.height}px`;
    });
  }

  _createPendingHotspot() {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tw-pending-hotspot';
    btn.setAttribute('aria-label', 'Configure this placeholder');
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.addEventListener('mousedown', (e) => e.preventDefault());
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const start = parseInt(btn.dataset.pendingStart, 10);
      if (isNaN(start)) return;
      const tokenId = this._findPendingTokenIdAt(start);
      if (tokenId) this._reopenToken(tokenId);
    });
    this.triggers.appendChild(btn);
    return btn;
  }

  _reopenToken(tokenId) {
    if (placeholders.get(tokenId)?.token === this._tagPlaceholder) {
      this._openTagDropdown(tokenId);
    } else {
      this._openPrimaryDropdown(tokenId);
    }
  }

  // Match a pending-placeholder text position back to a registered tokenId.
  // Stored `ph.start` may be stale after edits, so we verify against the
  // current text and fall back to a localized scan.
  _findPendingTokenIdAt(textStart) {
    const value = this.target.value;
    for (const [tokenId, ph] of placeholders) {
      if (ph.instance !== this) continue;
      if (ph.start === textStart && value.substr(ph.start, ph.token.length) === ph.token) {
        return tokenId;
      }
    }
    // Stale positions — resolve each placeholder's current start and retry.
    for (const [tokenId, ph] of placeholders) {
      if (ph.instance !== this) continue;
      let s = ph.start;
      if (value.substr(s, ph.token.length) !== ph.token) {
        s = value.indexOf(ph.token, Math.max(0, ph.start - 50));
        if (s === -1) continue;
      }
      if (s === textStart) {
        ph.start = s;
        ph.end = s + ph.token.length;
        return tokenId;
      }
    }
    return null;
  }

  _activeRange() {
    if (!this.activeTokenId) return null;
    const ph = placeholders.get(this.activeTokenId);
    if (!ph) return null;
    const value = this.target.value;
    if (value.substr(ph.start, ph.token.length) === ph.token) {
      return { start: ph.start, end: ph.start + ph.token.length };
    }
    const found = value.indexOf(ph.token, Math.max(ph.start - 50, 0));
    if (found === -1) return null;
    return { start: found, end: found + ph.token.length };
  }

  _syncOverlayScroll() {
    if (!this.overlay) return;
    this.overlay.scrollTop = this.target.scrollTop;
    this.overlay.scrollLeft = this.target.scrollLeft;
    this._updateActiveChip();
    this._updatePendingHotspots();
  }

  _bindEvents() {
    if (this.button) {
      this.button.addEventListener('click', (e) => {
        e.preventDefault();
        this._handleAddClick();
      });
    }

    this._outsideClickHandler = (e) => {
      if (this._disposeIfDetached()) return;
      if (!this.dropdown) return;
      if (this.dropdown.contains(e.target)) return;
      if (e.target === this.button) return;
      // Hotspots/chip inside the triggers layer handle their own reopen.
      if (this.triggers && this.triggers.contains(e.target)) return;
      // CM mode: clicking a pending placeholder span already reopens the
      // dropdown via _handleCmMouseDown — don't undo that by closing here.
      if (e.target instanceof Element && e.target.closest('.tw-hl--pending')) return;
      this._cancelDropdown();
    };
    this._keyHandler = (e) => {
      if (this._disposeIfDetached()) return;
      if (!this.dropdown) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        this._cancelDropdown();
        this.target.focus();
      }
    };
    this._resizeHandler = () => {
      if (this._disposeIfDetached()) return;
      this._positionDropdown();
      this._syncOverlayStyles();
      this._renderOverlay();
      if (this._cm) this._updateActiveChip();
    };
    // Body-appended dropdowns don't move with scrolling ancestors — follow along.
    this._scrollHandler = (e) => {
      if (this._disposeIfDetached()) return;
      if (!this.dropdown) return;
      if (e.target instanceof Element && this.dropdown.contains(e.target)) return;
      this._positionDropdown();
    };

    document.addEventListener('mousedown', this._outsideClickHandler);
    document.addEventListener('keydown', this._keyHandler);
    document.addEventListener('scroll', this._scrollHandler, true);
    window.addEventListener('resize', this._resizeHandler);

    // Keep the overlay in sync with user edits and scrolling.
    this.target.addEventListener('input', (e) => {
      this._renderOverlay();
      // Only real typing is an InputEvent — programmatic edits dispatch plain Events.
      if (this.openMenuOnBraces && e instanceof InputEvent && e.inputType === 'insertText') {
        this._maybeOpenBraceMenu();
      }
    });
    this.target.addEventListener('scroll', () => this._syncOverlayScroll());

    // CM's change event carries no input type; programmatic edits are excluded
    // by _suppressBraceTrigger and the caret-after-braces check.
    if (this.openMenuOnBraces && this._cm) {
      this._cmChangeOff = this._cm.on('change', () => this._maybeOpenBraceMenu());
    }

    if (this._cm) {
      this._cmChipOffs = ['selectionChange', 'change', 'scroll', 'focus'].map(
        (event) => this._cm.on(event, () => this._updateActiveChip())
      );
      this._cmChipOffs.push(this._cm.on('blur', () => this._hideActiveChip()));
    }

    // Caret-aware filter chip: reposition on anything that can move the caret.
    const updateChip = () => this._updateActiveChip();
    this.target.addEventListener('click', updateChip);
    this.target.addEventListener('keyup', updateChip);
    this.target.addEventListener('select', updateChip);
    this.target.addEventListener('focus', updateChip);
    this.target.addEventListener('blur', updateChip);

    // Observe target size changes (e.g. textarea drag-resize, flex layout
    // shifts) so the mirrored overlay and filter triggers re-align. Skip
    // when CM owns the visible layer — there is no overlay to keep aligned.
    if (!this._cm && typeof ResizeObserver !== 'undefined') {
      this._resizeObserver = new ResizeObserver(() => {
        this._syncOverlayStyles();
        this._renderOverlay();
      });
      this._resizeObserver.observe(this.target);
    }
  }

  // ---- Insertion flow (Phase 2) ------------------------------------------

  // Public entry point: open the primary insertion menu programmatically.
  // Consumers with their own CTA wire its click to call this on the
  // instance returned from `TwigBuilder.attach()` (or fetched via
  // `TwigBuilder.get()`).
  openInsertMenu() {
    this._handleAddClick();
  }

  _handleAddClick() {
    const tokenId = this._insertPlaceholder();
    this._openPlainFieldDropdown(tokenId, placeholders.get(tokenId)?.scope ?? null);
  }

  _insertPlaceholder() {
    const target = this.target;
    target.focus();

    const start = target.selectionStart ?? target.value.length;
    const end = target.selectionEnd ?? target.value.length;

    // Detect if the cursor is inside an existing {% for ... %} block so the
    // new placeholder inherits that loop's scope.
    const scope = detectScopeAtPosition(
      target.value,
      start,
      (listPath, parent) => this._resolveListItemShape(listPath, parent)
    );

    // setRangeText keeps native undo working in modern browsers
    target.setRangeText(this._placeholder, start, end, 'end');
    target.dispatchEvent(new Event('input', { bubbles: true }));

    const tokenId = `twg-${nextTokenId++}`;
    placeholders.set(tokenId, {
      instance: this,
      start,
      end: start + this._placeholder.length,
      scope,
      token: this._placeholder,
    });
    return tokenId;
  }

  // The user just completed `{{` or `{%`: swap the braces for a pending
  // placeholder and open the field dropdown or tag menu at that spot.
  _maybeOpenBraceMenu() {
    if (this._suppressBraceTrigger) return;

    const target = this.target;
    const pos = target.selectionStart ?? 0;
    if (pos < 2 || target.selectionEnd !== pos) return;

    const value = target.value;
    const typedText = value.slice(pos - 2, pos);
    if (typedText !== '{{' && typedText !== '{%') return;
    if (typedText === '{%' && !this.allowTags) return;
    if (value[pos - 3] === '{') return;
    if (this._insideTwigSegment(value, pos - 2)) return;

    const start = pos - 2;
    const scope = detectScopeAtPosition(
      value,
      start,
      (listPath, parent) => this._resolveListItemShape(listPath, parent)
    );

    const token = typedText === '{%' ? this._tagPlaceholder : this._placeholder;

    this._suppressBraceTrigger = true;
    target.setRangeText(token, start, pos, 'end');
    target.dispatchEvent(new Event('input', { bubbles: true }));
    this._suppressBraceTrigger = false;

    const tokenId = `twg-${nextTokenId++}`;
    placeholders.set(tokenId, {
      instance: this,
      start,
      end: start + token.length,
      scope,
      typed: true,
      typedText,
      token,
    });

    if (typedText === '{%') {
      this._openTagDropdown(tokenId);
    } else {
      this._openPlainFieldDropdown(tokenId, scope);
    }
  }

  _insideTwigSegment(value, position) {
    return scanTwigSegments(value).some(
      (segment) => segment.start < position && position < segment.end
    );
  }

  _removePendingPlaceholder(tokenId, replacement) {
    const ph = placeholders.get(tokenId);
    const value = this.target.value;

    let start = ph.start;
    if (value.substr(start, ph.token.length) !== ph.token) {
      start = value.indexOf(ph.token, Math.max(0, ph.start - 50));
    }

    placeholders.delete(tokenId);
    if (start === -1) return;

    this._suppressBraceTrigger = true;
    this.target.setRangeText(replacement, start, start + ph.token.length, 'end');
    this.target.dispatchEvent(new Event('input', { bubbles: true }));
    this._suppressBraceTrigger = false;
  }

  _openPrimaryDropdown(tokenId) {
    this._closeDropdown();
    this._setActive(tokenId);

    const dropdown = document.createElement('div');
    dropdown.className = 'twig-builder-dropdown';
    dropdown.setAttribute('role', 'listbox');
    dropdown.setAttribute('aria-label', 'Twig construct');

    const ph = placeholders.get(tokenId);
    const isNaked = !!ph?.naked;

    // Values section — anything that resolves to a concrete value at the
    // cursor (scope fields first, then root vars, set vars, literal entry).
    const valueItems = [];
    let scope = ph?.scope || null;
    while (scope) {
      const capturedScope = scope;
      valueItems.push({
        label: scopeFieldLabel(capturedScope.listPath, this.labels.scopeFieldSuffix),
        hint: capturedScope.listPath,
        suggested: true,
        handler: () => this._openPlainFieldDropdown(tokenId, capturedScope),
      });
      scope = scope.parent;
    }
    valueItems.push({
      label: this.labels.plainField,
      handler: () => this._openPlainFieldDropdown(tokenId, null),
    });
    if (isNaked) {
      valueItems.push({
        label: this.labels.typeValue,
        italic: true,
        handler: () => this._openLiteralInput(tokenId),
      });
    }

    const sections = [{ label: this.labels.sectionValues, items: valueItems }];

    // Logic section — block constructs. Not offered in naked (comparison)
    // contexts where only a value is valid, nor when the host forbids tags.
    if (!isNaked && this.allowTags) {
      sections.push({
        label: this.labels.sectionLogic,
        items: [
          { label: this.labels.forLoop, handler: () => this._openForLoopDropdown(tokenId) },
          { label: this.labels.ifElse, handler: () => this._openIfConditionDropdown(tokenId) },
        ],
      });
    }

    const showHeaders = sections.length > 1;

    sections.forEach((section) => {
      if (showHeaders) {
        const header = document.createElement('div');
        header.className = 'twig-builder-dropdown__section';
        header.textContent = section.label;
        dropdown.appendChild(header);
      }
      section.items.forEach((opt) => {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'twig-builder-dropdown__item';
        item.setAttribute('role', 'option');

        if (opt.italic) item.classList.add('twig-builder-dropdown__item--manual');
        if (opt.suggested) item.classList.add('twig-builder-dropdown__item--suggested');

        const label = document.createElement('span');
        label.textContent = opt.label;
        item.appendChild(label);

        if (opt.hint) {
          const hint = document.createElement('span');
          hint.className = 'twig-builder-dropdown__label';
          hint.textContent = opt.hint;
          item.appendChild(hint);
        }

        item.addEventListener('click', opt.handler);
        dropdown.appendChild(item);
      });
    });

    dropdown.addEventListener('keydown', (e) => this._onDropdownKey(e));

    this._mountDropdown(dropdown);
    dropdown.querySelector('.twig-builder-dropdown__item')?.focus();
  }

  _onDropdownKey(e, searchInput = null) {
    // When focus is inside an input (e.g. the search box), let its own
    // handler manage arrow keys.
    if (document.activeElement?.tagName === 'INPUT') return;

    const items = [
      ...this.dropdown.querySelectorAll(
        '.twig-builder-dropdown__item:not([disabled]):not(.twig-builder-dropdown__item--hidden)'
      ),
    ];
    const idx = items.indexOf(document.activeElement);
    const focus = (el) => {
      el?.focus();
      el?.scrollIntoView({ block: 'nearest' });
    };
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focus(items[Math.min(idx + 1, items.length - 1)]);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (idx <= 0 && searchInput) {
        searchInput.focus();
      } else {
        focus(items[idx - 1]);
      }
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      document.activeElement.click();
    }
  }

  // Small "← Back" link rendered at the top of each subdropdown. The returned
  // element handles its own mousedown/click wiring — callers just append it
  // and supply the destination in `onBack`.
  _createBackButton(onBack) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'twig-builder-dropdown__back';
    btn.textContent = this.labels.back;
    btn.setAttribute('aria-label', 'Back to previous menu');
    btn.addEventListener('mousedown', (e) => e.preventDefault());
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      onBack();
    });
    return btn;
  }

  // Returns the entries + the scope used. Pass `null` for root data; pass a
  // scope object to flatten that scope's item shape with the loopVar prefix.
  _scopedEntries(scope) {
    const isBranch = (path) => this._integrationFieldsTarget(path, scope) !== null;
    if (scope) {
      return {
        scope,
        entries: flattenShape(scope.shape, { prefix: scope.loopVar, isBranch }),
      };
    }
    return { scope: null, entries: flattenShape(this.data, { isBranch }) };
  }

  // Subtle inline preview of an entry's value when a sample order is loaded.
  // Returns null when there's nothing useful to show (default shape, list key
  // var, non-primitive, empty string).
  _previewValueFor(entry, scope) {
    if (entry.kind !== 'scalar') return null;

    let value;
    if (scope) {
      if (entry.path === scope.keyVar) return null;
      const prefix = scope.loopVar + '.';
      if (!entry.path.startsWith(prefix)) return null;
      value = resolveByPath(scope.shape, entry.path.slice(prefix.length));
    } else {
      value = resolveByPath(this.data, entry.path);
    }

    if (value === null || value === undefined) return null;
    if (typeof value === 'object') return null;

    const text = String(value);
    if (text === '') return null;

    const MAX = 40;

    return text.length > MAX ? text.slice(0, MAX - 1) + '…' : text;
  }

  // Given a list path (which may be scoped like "item.foo" or root like
  // "order_items"), return the shape of one item of that list, or null.
  _resolveListItemShape(listPath, parentScope) {
    if (!parentScope) {
      const list = resolveByPath(this.data, listPath);

      return Array.isArray(list) && list.length > 0 ? list[0] : null;
    }
    const prefix = parentScope.loopVar + '.';
    if (listPath.startsWith(prefix)) {
      const sub = listPath.slice(prefix.length);
      const list = resolveByPath(parentScope.shape, sub);
      return Array.isArray(list) && list.length > 0 ? list[0] : null;
    }
    return null;
  }

  // ---- For Loop flow (Phase 4) -------------------------------------------

  _openForLoopDropdown(tokenId) {
    this._closeDropdown();
    this._setActive(tokenId);

    const dropdown = document.createElement('div');
    dropdown.className = 'twig-builder-dropdown twig-builder-dropdown--scroll';
    dropdown.setAttribute('role', 'listbox');
    dropdown.setAttribute('aria-label', 'Select list to iterate');

    const header = document.createElement('div');
    header.className = 'twig-builder-dropdown__header';
    header.appendChild(this._createBackButton(() => this._openPrimaryDropdown(tokenId)));

    const search = document.createElement('input');
    search.type = 'search';
    search.className = 'twig-builder-dropdown__search';
    search.placeholder = 'Search lists…';
    search.setAttribute('aria-label', 'Search lists');
    search.autocomplete = 'off';
    search.spellcheck = false;
    header.appendChild(search);

    dropdown.appendChild(header);

    // Scoped iterable list paths
    const ph = placeholders.get(tokenId);
    const { entries } = this._scopedEntries(ph?.scope || null);
    const lists = entries.filter((e) => e.kind === 'list');

    const listWrap = document.createElement('div');
    listWrap.className = 'twig-builder-dropdown__list';
    dropdown.appendChild(listWrap);

    const empty = document.createElement('div');
    empty.className = 'twig-builder-dropdown__empty';
    dropdown.appendChild(empty);

    lists.forEach((entry) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'twig-builder-dropdown__item';
      item.setAttribute('role', 'option');
      item.dataset.path = entry.path;

      const path = document.createElement('span');
      path.className = 'twig-builder-dropdown__path';
      path.textContent = entry.path;
      item.appendChild(path);

      item.addEventListener('click', () => {
        this._insertForLoop(tokenId, entry.path);
      });
      listWrap.appendChild(item);
    });

    const updateEmpty = () => {
      if (lists.length === 0) {
        empty.textContent = 'No iterable lists available in this scope.';
        empty.style.display = 'block';
        return;
      }
      const anyVisible = !!listWrap.querySelector(
        '.twig-builder-dropdown__item[data-path]:not(.twig-builder-dropdown__item--hidden)'
      );
      empty.textContent = search.value.trim() ? 'No matches' : '';
      empty.style.display = anyVisible ? 'none' : 'block';
    };
    updateEmpty();

    search.addEventListener('input', () => {
      const q = search.value.trim().toLowerCase();
      listWrap.querySelectorAll('.twig-builder-dropdown__item[data-path]').forEach((it) => {
        const p = (it.dataset.path || '').toLowerCase();
        const match = !q || p.includes(q);
        it.classList.toggle('twig-builder-dropdown__item--hidden', !match);
      });
      updateEmpty();
    });

    search.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        const first = listWrap.querySelector(
          '.twig-builder-dropdown__item[data-path]:not([disabled]):not(.twig-builder-dropdown__item--hidden)'
        );
        first?.focus();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        const first = listWrap.querySelector(
          '.twig-builder-dropdown__item[data-path]:not([disabled]):not(.twig-builder-dropdown__item--hidden)'
        );
        first?.click();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this._cancelDropdown();
        this.target.focus();
      }
    });

    dropdown.addEventListener('keydown', (e) => this._onDropdownKey(e, search));
    this._mountDropdown(dropdown);
    search.focus();
  }

  _insertForLoop(tokenId, listPath) {
    this._insertBlock(tokenId, `{% for key, item in ${listPath} %}`, '{% endfor %}');
  }

  // Insert an empty block and land the caret in the middle, ready for a typed `{{`.
  _insertBlock(tokenId, opening, closing) {
    const body = this.isTextarea ? '\n  \n' : '  ';

    const { start } = this._replaceWithTemplate(tokenId, `${opening}${body}${closing}`, []);
    this._closeDropdown();

    if (start === -1) return;

    const caret = start + opening.length + (this.isTextarea ? 3 : 1);
    this.target.focus();
    this.target.setSelectionRange(caret, caret);
  }

  // ---- Tag flow (typed `{%`) ----------------------------------------------

  _openTagDropdown(tokenId) {
    this._closeDropdown();
    this._setActive(tokenId);

    const dropdown = document.createElement('div');
    dropdown.className = 'twig-builder-dropdown';
    dropdown.setAttribute('role', 'listbox');
    dropdown.setAttribute('aria-label', 'Twig tag');

    const tags = [
      { label: this.labels.forLoop, handler: () => this._openForLoopDropdown(tokenId) },
      { label: this.labels.ifElse, handler: () => this._openIfConditionDropdown(tokenId) },
    ];

    tags.forEach((tag) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'twig-builder-dropdown__item';
      item.setAttribute('role', 'option');

      const label = document.createElement('span');
      label.textContent = tag.label;
      item.appendChild(label);

      item.addEventListener('click', tag.handler);
      dropdown.appendChild(item);
    });

    dropdown.addEventListener('keydown', (e) => this._onDropdownKey(e));
    this._mountDropdown(dropdown);
    dropdown.querySelector('.twig-builder-dropdown__item')?.focus();
  }

  // Condition first, insertion after: a pending placeholder inside a `{% %}`
  // segment would be unreachable by pending styling and hotspots.
  _openIfConditionDropdown(tokenId) {
    this._closeDropdown();
    this._setActive(tokenId);

    const dropdown = document.createElement('div');
    dropdown.className = 'twig-builder-dropdown twig-builder-dropdown--scroll';
    dropdown.setAttribute('role', 'listbox');
    dropdown.setAttribute('aria-label', 'Select condition');

    const header = document.createElement('div');
    header.className = 'twig-builder-dropdown__header';
    header.appendChild(this._createBackButton(() => this._openPrimaryDropdown(tokenId)));

    const elseRow = document.createElement('label');
    elseRow.className = 'twig-builder-key-toggle';
    const elseBox = document.createElement('input');
    elseBox.type = 'checkbox';
    elseRow.appendChild(elseBox);
    const elseText = document.createElement('span');
    elseText.textContent = `${this.labels.ifIncludeElse}  `;
    elseRow.appendChild(elseText);
    const elseHint = document.createElement('code');
    elseHint.textContent = '{% else %}';
    elseHint.className = 'twig-builder-key-toggle__hint';
    elseRow.appendChild(elseHint);
    header.appendChild(elseRow);

    const manual = document.createElement('button');
    manual.type = 'button';
    manual.className = 'twig-builder-dropdown__item twig-builder-dropdown__item--manual';
    manual.setAttribute('role', 'option');
    manual.textContent = this.labels.enterManually;
    manual.addEventListener('click', () => this._openIfConditionInput(tokenId, elseBox.checked));

    const search = document.createElement('input');
    search.type = 'search';
    search.className = 'twig-builder-dropdown__search';
    search.placeholder = 'Search…';
    search.setAttribute('aria-label', 'Search fields');
    search.autocomplete = 'off';
    search.spellcheck = false;

    const searchRow = document.createElement('div');
    searchRow.className = 'twig-builder-dropdown__search-row';
    searchRow.appendChild(search);
    searchRow.appendChild(manual);
    header.appendChild(searchRow);

    dropdown.appendChild(header);

    const ph = placeholders.get(tokenId);
    const { entries } = this._scopedEntries(ph?.scope || null);

    const listWrap = document.createElement('div');
    listWrap.className = 'twig-builder-dropdown__list';
    dropdown.appendChild(listWrap);

    entries.forEach((entry) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'twig-builder-dropdown__item';
      item.setAttribute('role', 'option');
      item.dataset.path = entry.path;
      if (entry.depth > 0) {
        item.style.paddingLeft = `${12 + entry.depth * 14}px`;
      }

      const path = document.createElement('span');
      path.className = 'twig-builder-dropdown__path';
      path.textContent = entry.path;
      item.appendChild(path);

      item.addEventListener('click', () => {
        this._insertIfBlock(tokenId, entry.path, elseBox.checked);
      });
      listWrap.appendChild(item);
    });

    search.addEventListener('input', () => {
      const q = search.value.trim().toLowerCase();
      listWrap.querySelectorAll('.twig-builder-dropdown__item[data-path]').forEach((it) => {
        const p = (it.dataset.path || '').toLowerCase();
        it.classList.toggle('twig-builder-dropdown__item--hidden', q && !p.includes(q));
      });
    });

    search.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        listWrap.querySelector(
          '.twig-builder-dropdown__item[data-path]:not(.twig-builder-dropdown__item--hidden)'
        )?.focus();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        listWrap.querySelector(
          '.twig-builder-dropdown__item[data-path]:not(.twig-builder-dropdown__item--hidden)'
        )?.click();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this._cancelDropdown();
        this.target.focus();
      }
    });

    dropdown.addEventListener('keydown', (e) => this._onDropdownKey(e, search));
    this._mountDropdown(dropdown);
    search.focus();
  }

  _openIfConditionInput(tokenId, includeElse = false) {
    this._closeDropdown();
    this._setActive(tokenId);

    const dropdown = document.createElement('div');
    dropdown.className = 'twig-builder-dropdown twig-builder-dropdown--manual';

    dropdown.appendChild(this._createBackButton(() => this._openIfConditionDropdown(tokenId)));

    const heading = document.createElement('div');
    heading.className = 'twig-builder-dropdown__heading';
    heading.textContent = this.labels.ifConditionHeading;
    dropdown.appendChild(heading);

    const form = document.createElement('div');
    form.className = 'twig-builder-manual-form';

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'twig-builder-manual-input';
    input.placeholder = 'e.g. order.itemPriceTotal > 100';
    form.appendChild(input);

    const submit = document.createElement('button');
    submit.type = 'button';
    submit.className = 'twig-builder-manual-submit';
    submit.textContent = this.labels.insert;
    form.appendChild(submit);

    dropdown.appendChild(form);

    const commit = () => {
      const condition = input.value.trim();
      if (!condition) { input.focus(); return; }
      this._insertIfBlock(tokenId, condition, includeElse);
    };

    submit.addEventListener('click', commit);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); commit(); }
      else if (e.key === 'Escape') {
        e.preventDefault();
        this._cancelDropdown();
        this.target.focus();
      }
    });

    this._mountDropdown(dropdown);
    input.focus();
  }

  _insertIfBlock(tokenId, condition, includeElse = false) {
    const closing = includeElse
      ? (this.isTextarea ? '{% else %}\n  \n{% endif %}' : '{% else %}  {% endif %}')
      : '{% endif %}';

    this._insertBlock(tokenId, `{% if ${condition} %}`, closing);
  }


  // ---- Plain Field flow (Phase 3) ----------------------------------------

  _openPlainFieldDropdown(tokenId, scope = null) {
    this._closeDropdown();
    this._setActive(tokenId);

    const dropdown = document.createElement('div');
    dropdown.className =
      'twig-builder-dropdown twig-builder-dropdown--scroll';
    dropdown.setAttribute('role', 'listbox');
    dropdown.setAttribute('aria-label', 'Select Twig variable');

    // No back link when the primary menu offers nothing beyond plain fields.
    const header = document.createElement('div');
    header.className = 'twig-builder-dropdown__header';

    const ph = placeholders.get(tokenId);
    if (ph?.scope || ph?.naked || this.allowTags) {
      header.appendChild(this._createBackButton(() => this._openPrimaryDropdown(tokenId)));
    }

    const manual = document.createElement('button');
    manual.type = 'button';
    manual.className =
      'twig-builder-dropdown__item twig-builder-dropdown__item--manual';
    manual.setAttribute('role', 'option');
    manual.textContent = this.labels.enterManually;
    manual.addEventListener('click', () => this._openManualInput(tokenId));

    const search = document.createElement('input');
    search.type = 'search';
    search.className = 'twig-builder-dropdown__search';
    search.placeholder = 'Search…';
    search.setAttribute('aria-label', 'Search variables');
    search.autocomplete = 'off';
    search.spellcheck = false;

    const searchRow = document.createElement('div');
    searchRow.className = 'twig-builder-dropdown__search-row';
    searchRow.appendChild(search);
    searchRow.appendChild(manual);
    header.appendChild(searchRow);

    dropdown.appendChild(header);

    // Variable list — `scope` null = root, otherwise that scope's item shape.
    const { scope: useScope, entries: rawEntries } = this._scopedEntries(scope);
    const entries = [];
    if (useScope?.keyVar) {
      entries.push({ path: useScope.keyVar, kind: 'scalar', depth: 0 });
    }
    entries.push(...rawEntries);

    const listWrap = document.createElement('div');
    listWrap.className = 'twig-builder-dropdown__list';
    dropdown.appendChild(listWrap);

    const empty = document.createElement('div');
    empty.className = 'twig-builder-dropdown__empty';
    dropdown.appendChild(empty);

    entries.forEach((entry) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'twig-builder-dropdown__item';
      item.setAttribute('role', 'option');
      item.dataset.path = entry.path;
      if (entry.depth > 0) {
        item.style.paddingLeft = `${12 + entry.depth * 14}px`;
      }

      const path = document.createElement('span');
      path.className = 'twig-builder-dropdown__path';
      path.textContent = entry.path;
      item.appendChild(path);

      const integrationFieldsTarget = this._integrationFieldsTarget(entry.path, useScope);

      if (integrationFieldsTarget !== null) {
        const label = document.createElement('span');
        label.className = 'twig-builder-dropdown__label';
        label.textContent = this.labels.integrationFieldPick;
        item.appendChild(label);

        item.addEventListener('click', () => {
          this._openIntegrationFieldPicker(tokenId, entry.path, integrationFieldsTarget, scope);
        });
      } else if (this._isMapPath(entry.path, useScope)) {
        const label = document.createElement('span');
        label.className = 'twig-builder-dropdown__label';
        label.textContent = this.labels.mapEnterKey;
        item.appendChild(label);

        item.addEventListener('click', () => {
          this._openMapKeyInput(tokenId, entry.path, scope);
        });
      } else if (entry.kind === 'list') {
        item.classList.add('twig-builder-dropdown__item--list');
        const label = document.createElement('span');
        label.className = 'twig-builder-dropdown__label';
        item.disabled = true;
        item.setAttribute('aria-disabled', 'true');
        label.textContent = this.labels.listWontRender;
        item.appendChild(label);
      } else {
        const preview = this._previewValueFor(entry, useScope);
        if (preview !== null) {
          const hint = document.createElement('span');
          hint.className = 'twig-builder-dropdown__preview';
          hint.textContent = preview;
          hint.title = preview;
          item.appendChild(hint);
        }

        item.addEventListener('click', () => {
          this._commitVariable(tokenId, entry.path);
        });
      }

      listWrap.appendChild(item);
    });

    const updateEmpty = () => {
      if (entries.length === 0) {
        empty.textContent = 'No data available. Attach with TwigBuilder.attach(el, { data })';
        empty.style.display = 'block';
        return;
      }
      const anyVisible = !!dropdown.querySelector(
        '.twig-builder-dropdown__item[data-path]:not(.twig-builder-dropdown__item--hidden)'
      );
      empty.textContent = search.value.trim() ? 'No matches' : '';
      empty.style.display = anyVisible ? 'none' : 'block';
    };
    updateEmpty();

    search.addEventListener('input', () => {
      const q = search.value.trim().toLowerCase();
      listWrap.querySelectorAll('.twig-builder-dropdown__item[data-path]').forEach((it) => {
        const p = (it.dataset.path || '').toLowerCase();
        const match = !q || p.includes(q);
        it.classList.toggle('twig-builder-dropdown__item--hidden', !match);
      });
      updateEmpty();
    });

    // Keyboard transitions between search and list
    search.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        const first = listWrap.querySelector(
          '.twig-builder-dropdown__item[data-path]:not([disabled]):not(.twig-builder-dropdown__item--hidden)'
        );
        first?.focus();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        const first = listWrap.querySelector(
          '.twig-builder-dropdown__item[data-path]:not([disabled]):not(.twig-builder-dropdown__item--hidden)'
        );
        first?.click();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this._cancelDropdown();
        this.target.focus();
      }
    });

    dropdown.addEventListener('keydown', (e) => this._onDropdownKey(e, search));
    this._mountDropdown(dropdown);
    search.focus();
  }

  // Open-keyed maps (custom fields, metadata) have no keys to list — the user
  // supplies one and the path is built, bracket-quoted when needed.
  _openMapKeyInput(tokenId, basePath, scope = null) {
    this._closeDropdown();
    this._setActive(tokenId);

    const dropdown = document.createElement('div');
    dropdown.className = 'twig-builder-dropdown twig-builder-dropdown--manual';

    dropdown.appendChild(this._createBackButton(() => this._openPlainFieldDropdown(tokenId, scope)));

    const heading = document.createElement('div');
    heading.className = 'twig-builder-dropdown__heading';
    heading.textContent = `${basePath}: ${this.labels.mapKeyHeading}`;
    dropdown.appendChild(heading);

    const form = document.createElement('div');
    form.className = 'twig-builder-manual-form';

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'twig-builder-manual-input';
    input.placeholder = this.labels.mapKeyPlaceholder;
    form.appendChild(input);

    const submit = document.createElement('button');
    submit.type = 'button';
    submit.className = 'twig-builder-manual-submit';
    submit.textContent = this.labels.insert;
    form.appendChild(submit);

    dropdown.appendChild(form);

    const commit = () => {
      const key = input.value.trim();
      if (!key) { input.focus(); return; }
      this._commitVariable(tokenId, joinPath(basePath, key));
    };

    submit.addEventListener('click', commit);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); commit(); }
      else if (e.key === 'Escape') {
        e.preventDefault();
        this._cancelDropdown();
        this.target.focus();
      }
    });

    this._mountDropdown(dropdown);
    input.focus();
  }

  // Integration fields nest one map per integration stub. The server renders
  // the automation key selector and resolves the chosen field, so a
  // parameterized key comes back with its arguments filled in.
  _openIntegrationFieldPicker(tokenId, basePath, target, scope = null) {
    this._closeDropdown();
    this._setActive(tokenId);

    const urls = this.integrationFieldUrls[target];

    const dropdown = document.createElement('div');
    dropdown.className = 'twig-builder-dropdown twig-builder-dropdown--manual';

    dropdown.appendChild(this._createBackButton(() => this._openPlainFieldDropdown(tokenId, scope)));

    const heading = document.createElement('div');
    heading.className = 'twig-builder-dropdown__heading';
    heading.textContent = `${basePath}: ${this.labels.integrationFieldTitle}`;
    dropdown.appendChild(heading);

    const body = document.createElement('div');
    body.className = 'twig-builder-integration-fields';
    dropdown.appendChild(body);

    const submit = document.createElement('button');
    submit.type = 'button';
    submit.className = 'twig-builder-manual-submit';
    submit.textContent = this.labels.insert;
    submit.hidden = true;
    dropdown.appendChild(submit);

    let request = 0;

    const selection = () => {
      const params = new URLSearchParams();
      body
        .querySelectorAll('[name="integration"], [name="key"], [name="keyArguments[]"]')
        .forEach((field) => params.append(field.name, field.value));
      return params;
    };

    const show = (html) => {
      body.innerHTML = html;
      submit.hidden = body.querySelector('[name="key"]') === null;
      this._positionDropdown();
    };

    const load = async (params) => {
      const current = ++request;
      const response = await fetch(`${urls.picker}?${params}`, { credentials: 'same-origin' });
      if (current !== request || this.dropdown !== dropdown) return;
      const html = await response.text();
      if (current !== request || this.dropdown !== dropdown) return;
      show(html);
    };

    body.addEventListener('change', (e) => {
      const changed = e.target.closest('[name="integration"], [name="key"]');
      if (!changed) return;
      const params = selection();
      if (changed.name === 'integration') {
        params.delete('key');
        params.delete('keyArguments[]');
      }
      load(params);
    });

    submit.addEventListener('click', async () => {
      const current = ++request;
      const response = await fetch(`${urls.path}?${selection()}`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (current !== request || this.dropdown !== dropdown) return;
      if (!response.ok) {
        const html = await response.text();
        if (current !== request || this.dropdown !== dropdown) return;
        show(html);
        return;
      }
      const { stub, key } = await response.json();
      if (current !== request || this.dropdown !== dropdown) return;
      this._commitVariable(tokenId, `${joinPath(`${basePath}.${stub}`, key)}|default('')`);
    });

    this._mountDropdown(dropdown);
    load(new URLSearchParams());
  }

  _openManualInput(tokenId) {
    this._closeDropdown();
    this._setActive(tokenId);

    const dropdown = document.createElement('div');
    dropdown.className =
      'twig-builder-dropdown twig-builder-dropdown--manual';

    dropdown.appendChild(this._createBackButton(() => this._openPlainFieldDropdown(tokenId)));

    const heading = document.createElement('div');
    heading.className = 'twig-builder-dropdown__heading';
    heading.textContent = this.labels.manualInputHeading;
    dropdown.appendChild(heading);

    const form = document.createElement('div');
    form.className = 'twig-builder-manual-form';

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'twig-builder-manual-input';
    input.placeholder = 'e.g. order.emailAddress';
    form.appendChild(input);

    const submit = document.createElement('button');
    submit.type = 'button';
    submit.className = 'twig-builder-manual-submit';
    submit.textContent = this.labels.insert;
    form.appendChild(submit);

    dropdown.appendChild(form);

    const commit = () => {
      const name = input.value.trim();
      if (!name) { input.focus(); return; }
      this._commitVariable(tokenId, name);
    };

    submit.addEventListener('click', commit);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); commit(); }
      else if (e.key === 'Escape') {
        e.preventDefault();
        this._cancelDropdown();
        this.target.focus();
      }
    });

    this._mountDropdown(dropdown);
    input.focus();
  }

  // Locate the {{ please select }} belonging to `tokenId` and swap it for
  // `template`. For any `{{ please select }}` tokens inside `template`,
  // register new placeholders with the supplied configs (scope + flags).
  // Returns { start, end, newIds } — start/end are the span of the inserted
  // template, useful for appending text (e.g. operators) after.
  _replaceWithTemplate(tokenId, template, innerConfigs = []) {
    const ph = placeholders.get(tokenId);
    if (!ph || ph.instance !== this) return { start: -1, end: -1, newIds: [] };
    const target = this.target;
    const value = target.value;

    let start = ph.start;
    if (value.substr(start, ph.token.length) !== ph.token) {
      start = value.indexOf(ph.token, ph.start);
      if (start === -1) start = value.indexOf(ph.token);
    }
    if (start === -1) return { start: -1, end: -1, newIds: [] };

    target.focus();
    target.setRangeText(template, start, start + ph.token.length, 'end');
    target.dispatchEvent(new Event('input', { bubbles: true }));
    placeholders.delete(tokenId);

    const newIds = this._registerPlaceholdersInRange(start, start + template.length, innerConfigs);
    return { start, end: start + template.length, newIds };
  }

  // Insert `text` at position `pos` without replacing anything. Register any
  // `{{ please select }}` inside with configs and return new tokenIds.
  _insertTextAt(pos, text, innerConfigs = []) {
    const target = this.target;
    target.focus();
    target.setRangeText(text, pos, pos, 'end');
    target.dispatchEvent(new Event('input', { bubbles: true }));
    return this._registerPlaceholdersInRange(pos, pos + text.length, innerConfigs);
  }

  _registerPlaceholdersInRange(rangeStart, rangeEnd, configs) {
    const value = this.target.value;
    const newIds = [];
    let scanFrom = rangeStart;
    for (const cfg of configs) {
      const found = value.indexOf(this._placeholder, scanFrom);
      if (found === -1 || found >= rangeEnd) break;
      const id = `twg-${nextTokenId++}`;
      placeholders.set(id, {
        instance: this,
        start: found,
        end: found + this._placeholder.length,
        scope: cfg?.scope || null,
        naked: !!cfg?.naked,
        token: this._placeholder,
      });
      newIds.push(id);
      scanFrom = found + this._placeholder.length;
    }
    return newIds;
  }

  _replacePlaceholder(tokenId, replacement) {
    this._replaceWithTemplate(tokenId, replacement, []);
  }

  // Small typed-value input offered for naked placeholders (condition sides).
  _openLiteralInput(tokenId) {
    this._closeDropdown();
    this._setActive(tokenId);

    const dropdown = document.createElement('div');
    dropdown.className = 'twig-builder-dropdown twig-builder-dropdown--manual';

    dropdown.appendChild(this._createBackButton(() => this._openPrimaryDropdown(tokenId)));

    const heading = document.createElement('div');
    heading.className = 'twig-builder-dropdown__heading';
    heading.textContent = this.labels.literalInputHeading;
    dropdown.appendChild(heading);

    const form = document.createElement('div');
    form.className = 'twig-builder-manual-form';

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'twig-builder-manual-input';
    input.placeholder = "e.g. active, 5, true (strings auto-quoted)";
    form.appendChild(input);

    const submit = document.createElement('button');
    submit.type = 'button';
    submit.className = 'twig-builder-manual-submit';
    submit.textContent = this.labels.insert;
    form.appendChild(submit);

    dropdown.appendChild(form);

    const commit = () => {
      const formatted = formatTwigLiteral(input.value);
      if (!formatted) { input.focus(); return; }
      this._commitVariable(tokenId, formatted);
    };

    submit.addEventListener('click', commit);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); commit(); }
      else if (e.key === 'Escape') {
        e.preventDefault();
        this._cancelDropdown();
        this.target.focus();
      }
    });

    this._mountDropdown(dropdown);
    input.focus();
  }

  // ---- Filters (Phase 6) -------------------------------------------------

  _openFilterDropdown(varRange) {
    this._closeDropdown();
    this._filterRange = varRange;

    const dropdown = document.createElement('div');
    dropdown.className = 'twig-builder-dropdown twig-builder-dropdown--scroll';
    dropdown.setAttribute('role', 'listbox');
    dropdown.setAttribute('aria-label', 'Twig filter');

    const header = document.createElement('div');
    header.className = 'twig-builder-dropdown__header';

    const hint = document.createElement('p');
    hint.className = 'twig-builder-dropdown__hint';
    hint.textContent = this.labels.filterMenuHint;
    header.appendChild(hint);

    const search = document.createElement('input');
    search.type = 'search';
    search.className = 'twig-builder-dropdown__search';
    search.placeholder = 'Search filters…';
    search.autocomplete = 'off';
    search.spellcheck = false;
    header.appendChild(search);
    dropdown.appendChild(header);

    const listWrap = document.createElement('div');
    listWrap.className = 'twig-builder-dropdown__list';
    dropdown.appendChild(listWrap);

    this.filters.forEach((filter) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'twig-builder-dropdown__item';
      item.setAttribute('role', 'option');
      item.dataset.name = filter.name;

      const name = document.createElement('span');
      name.className = 'twig-builder-dropdown__path';
      name.textContent = filter.name + (filter.arg ? '(…)' : '');
      item.appendChild(name);

      if (filter.desc) {
        const desc = document.createElement('span');
        desc.className = 'twig-builder-dropdown__label';
        desc.textContent = filter.desc;
        item.appendChild(desc);
      }

      item.addEventListener('click', () => {
        if (filter.arg) this._openFilterArgInput(varRange, filter);
        else this._applyFilter(varRange, filter.name, null);
      });
      listWrap.appendChild(item);
    });

    search.addEventListener('input', () => {
      const q = search.value.trim().toLowerCase();
      listWrap.querySelectorAll('.twig-builder-dropdown__item').forEach((it) => {
        const n = (it.dataset.name || '').toLowerCase();
        it.classList.toggle('twig-builder-dropdown__item--hidden', q && !n.includes(q));
      });
    });

    search.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        listWrap.querySelector(
          '.twig-builder-dropdown__item:not(.twig-builder-dropdown__item--hidden)'
        )?.focus();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        listWrap.querySelector(
          '.twig-builder-dropdown__item:not(.twig-builder-dropdown__item--hidden)'
        )?.click();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this._cancelDropdown();
        this.target.focus();
      }
    });

    dropdown.addEventListener('keydown', (e) => this._onDropdownKey(e, search));
    this._mountDropdown(dropdown);
    search.focus();
  }

  _openFilterArgInput(varRange, filter) {
    this._closeDropdown();
    this._filterRange = varRange;

    const dropdown = document.createElement('div');
    dropdown.className = 'twig-builder-dropdown twig-builder-dropdown--manual';

    dropdown.appendChild(this._createBackButton(() => this._openFilterDropdown(varRange)));

    const heading = document.createElement('div');
    heading.className = 'twig-builder-dropdown__heading';
    heading.textContent = `${filter.name}: ${filter.arg}`;
    dropdown.appendChild(heading);

    const form = document.createElement('div');
    form.className = 'twig-builder-manual-form';
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'twig-builder-manual-input';
    input.placeholder = 'value (strings auto-quoted)';
    form.appendChild(input);
    const submit = document.createElement('button');
    submit.type = 'button';
    submit.className = 'twig-builder-manual-submit';
    submit.textContent = this.labels.apply;
    form.appendChild(submit);
    dropdown.appendChild(form);

    const commit = () => {
      const arg = formatTwigLiteral(input.value);
      if (!arg) { input.focus(); return; }
      this._applyFilter(varRange, filter.name, arg);
    };
    submit.addEventListener('click', commit);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); commit(); }
      else if (e.key === 'Escape') {
        e.preventDefault();
        this._cancelDropdown();
        this.target.focus();
      }
    });

    this._mountDropdown(dropdown);
    input.focus();
  }

  // Append `|filterName` (or `|filterName(arg)`) inside the {{ … }} at
  // `varRange`, preserving existing internal spacing and earlier filters.
  _applyFilter(varRange, filterName, arg) {
    const clause = arg != null ? `|${filterName}(${arg})` : `|${filterName}`;
    const edit   = buildFilterEdit(this.target.value, varRange, clause);

    this.target.focus();
    this.target.setRangeText(edit.insert, edit.from, edit.to, 'end');
    this.target.dispatchEvent(new Event('input', { bubbles: true }));
    this._closeDropdown();
  }

  // Finalize a variable choice from Plain Field / Manual input. Honors the
  // placeholder's `naked` flag (for conditions, no `{{ }}` wrapping).
  // Datetime fields auto-suffix `|date('<store format>')` so the output
  // renders the way the store has configured its dates.
  _commitVariable(tokenId, path) {
    const ph = placeholders.get(tokenId);
    if (!ph) { this._closeDropdown(); return; }
    const { naked, scope } = ph;

    let rendered = path;
    if (!naked && this._isDatetimePath(path, scope) && this.dateTimeFormat) {
      const fmt = this.dateTimeFormat.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
      rendered = `${path}|date('${fmt}')`;
    }

    const replacement = naked ? path : `{{ ${rendered} }}`;
    this._replaceWithTemplate(tokenId, replacement, []);
    this._closeDropdown();
    this.target.focus();
  }

  // Resolve a (possibly scope-relative) path to its full data-tree path
  // and look it up in the types map. Walks up nested for-loop scopes so
  // `shipment.shippedAt` inside `{% for shipment in order.shipments %}`
  // resolves to `order.shipments.0.shippedAt`.
  _isDatetimePath(path, scope) {
    return this.fieldTypes[this._resolveTypePath(path, scope)] === 'datetime';
  }

  _integrationFieldsTarget(path, scope) {
    const type = this.fieldTypes[this._resolveTypePath(path, scope)] ?? '';
    return type.startsWith('integrationFields:') ? type.slice('integrationFields:'.length) : null;
  }

  _isMapPath(path, scope) {
    return this.fieldTypes[this._resolveTypePath(path, scope)] === 'map';
  }

  _resolveTypePath(path, scope) {
    if (!scope) return path;
    const prefix = scope.loopVar + '.';
    if (path.startsWith(prefix)) {
      return this._resolveTypePath(scope.listPath, scope.parent) + '.0.' + path.slice(prefix.length);
    }
    return this._resolveTypePath(path, scope.parent);
  }

  // Every dropdown mounts through here: enforces the single-open-menu rule
  // and contains clicks — outside-click handlers (e.g. side panel masks)
  // must not see clicks inside a body-appended menu.
  _mountDropdown(dropdown) {
    if (activeDropdownInstance && activeDropdownInstance !== this) {
      activeDropdownInstance._cancelDropdown();
    }
    activeDropdownInstance = this;

    dropdown.addEventListener('mousedown', (e) => e.stopPropagation());
    dropdown.addEventListener('click', (e) => e.stopPropagation());

    document.body.appendChild(dropdown);
    this.dropdown = dropdown;
    this._lockedWidth = 0;
    this._positionDropdown();
    // Pin the floor to the full-list width so filtering the list can't narrow
    // the menu — it auto-sizes on open, then holds that width.
    this._lockedWidth = dropdown.offsetWidth;
    dropdown.style.minWidth = `${this._lockedWidth}px`;
  }

  _positionDropdown() {
    if (!this.dropdown) return;
    const rect = this._anchorRect();
    this.dropdown.style.minWidth = `${Math.max(rect.width, 180, this._lockedWidth || 0)}px`;

    const maxLeft = window.scrollX + document.documentElement.clientWidth - this.dropdown.offsetWidth - 8;
    const left = Math.min(window.scrollX + rect.left, maxLeft);

    const height = this.dropdown.offsetHeight;
    const fitsBelow = rect.bottom + 4 + height <= document.documentElement.clientHeight;
    const fitsAbove = rect.top - 4 - height >= 0;
    const top = !fitsBelow && fitsAbove
      ? window.scrollY + rect.top - height - 4
      : window.scrollY + rect.bottom + 4;

    this.dropdown.style.top = `${top}px`;
    this.dropdown.style.left = `${Math.max(window.scrollX + 8, left)}px`;
  }

  // Prefer anchoring the dropdown directly under the active placeholder so
  // the menu sits next to the `{{ please select }}` it configures. Falls
  // back to the editor element when no usable position is available.
  _anchorRect() {
    if (this._cm && this._filterRange) {
      const rect = this._cmRangeRect(this._filterRange.start, this._filterRange.end);
      if (rect) {
        return rect;
      }
    }
    if (this.activeTokenId) {
      const ph = placeholders.get(this.activeTokenId);
      if (ph && ph.instance === this) {
        if (this._cm) {
          const rect = this._cmRangeRect(ph.start, ph.end);
          if (rect) {
            return rect;
          }
        } else if (this.overlay) {
          const span = this.overlay.querySelector(
            `.tw-hl--pending[data-pending-start="${ph.start}"]`
          );
          if (span) {
            const rects = span.getClientRects();
            const first = rects[0];
            if (first && (first.width > 0 || first.height > 0)) {
              return first;
            }
          }
        }
      }
    }
    return this.target.getBoundingClientRect();
  }

  _cmRangeRect(from, to) {
    const start = this._cm.view.coordsAtPos(from);
    const end   = this._cm.view.coordsAtPos(to);

    if (!start || !end) {
      return null;
    }

    return {
      top:    start.top,
      bottom: end.bottom,
      left:   start.left,
      right:  end.right,
      width:  Math.max(0, end.right - start.left),
      height: Math.max(0, end.bottom - start.top),
    };
  }

  // Dismissal, as opposed to a menu transition: the pending placeholder never
  // survives — typed braces revert, menu-opened tokens are removed.
  _cancelDropdown() {
    const ph = this.activeTokenId ? placeholders.get(this.activeTokenId) : null;
    if (ph && ph.instance === this) {
      this._removePendingPlaceholder(this.activeTokenId, ph.typed ? ph.typedText : '');
    }
    this._closeDropdown();
  }

  _closeDropdown() {
    if (this.dropdown) {
      this.dropdown.remove();
      this.dropdown = null;
    }
    this._lockedWidth = 0;
    this._filterRange = null;
    if (activeDropdownInstance === this) {
      activeDropdownInstance = null;
    }
    this._setActive(null);
  }

  // Nothing calls a teardown hook when host pages discard wrapped fields, so
  // the document/window listeners self-dispose on their first event after.
  _disposeIfDetached() {
    if (this.wrapper?.isConnected) return false;

    document.removeEventListener('mousedown', this._outsideClickHandler);
    document.removeEventListener('keydown', this._keyHandler);
    document.removeEventListener('scroll', this._scrollHandler, true);
    window.removeEventListener('resize', this._resizeHandler);
    this._resizeObserver?.disconnect();
    this._cmChangeOff?.();
    this._cmChipOffs?.forEach((off) => off());
    this._closeDropdown();

    return true;
  }

  _setActive(tokenId) {
    if (this.activeTokenId === tokenId) return;
    this.activeTokenId = tokenId;
    this._renderOverlay();
  }
}

export default TwigBuilder;
export { TwigBuilder };
